import React, {useMemo, useState} from 'react';
import {Layers} from 'lucide-react';

const CATEGORIES = [
  ['results', 'Results & Events', 'Match results, round differences and event counts'],
  ['pistols', 'Pistols', 'Pistol rounds and map conversion rates'],
  ['conversions', 'Round Conversions', 'Round two, bonus and anti-bonus outcomes'],
  ['players', 'Players & Lineups', 'Player, agent and player combination performance'],
  ['comps', 'Team Compositions', 'Agent lineups and saved composition performance'],
  ['strategies', 'Round Patterns & Strategies', 'Recorded strategic outcomes'],
  ['focus', 'Focus Points', 'Ratings, trends and review participation'],
  ['improvements', 'Improvement Points', 'Active points, resolutions and reopenings'],
  ['coverage', 'Data Completeness', 'Coverage of the recorded match data'],
];

function categoryFor(row) {
  const id = row.id || '';
  if (id.startsWith('focus') || row.studioView === 'focus') return 'focus';
  if (id.startsWith('improvement:') || row.studioView === 'improvements') return 'improvements';
  if (id.startsWith('coverage:') || row.studioView === 'coverage') return 'coverage';
  if (row.studioView === 'players') return 'players';
  if (row.studioView === 'comps') return 'comps';
  return {Core:'results', Pistols:'pistols', Conversions:'conversions', Roster:'players', Comps:'comps', Strategies:'strategies'}[row.category] || 'results';
}

export default function StudioMetricLibrary({rows, getMeta, getKey, selectedKeys, onAdd, onStarter, Info}) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({results:true});
  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return CATEGORIES.map(([id, title, description]) => ({id, title, description,
      rows: rows.filter(row => categoryFor(row) === id && (!query ||
        [getMeta(row).title, getMeta(row).definition, title, row.playerName, row.compName, row.mapName].join(' ').toLowerCase().includes(query)))
    })).filter(group => group.rows.length);
  }, [rows, getMeta, search]);

  return <section className="--TSD-metric-library" aria-label="Statistics library">
    <div className="--TSD-library-heading"><h2><Layers size={17}/>Metric library</h2><button type="button" className="--TSD-text-button" onClick={onStarter}>Starter Set</button></div>
    <label className="--TSD-library-search"><span>Search statistics</span><input type="search" placeholder="Search statistics…" value={search} onChange={event=>setSearch(event.target.value)}/></label>
    <p className="--TSD-library-help">Add a metric, then choose its data filters.</p>
    <div className="--TSD-library-categories">
      {!groups.length && <p className="--TSD-library-help" role="status">No statistics match your search.</p>}
      {groups.map(group => {
        const open = Boolean(search.trim()) || expanded[group.id];
        return <div className="--TSD-library-category" key={group.id}>
          <button type="button" className="--TSD-library-category-toggle" aria-expanded={Boolean(open)} aria-controls={`studio-library-${group.id}`} onClick={()=>setExpanded(current=>({...current,[group.id]:!open}))}>
            <span>{group.title}</span><small>{group.rows.length}</small><span aria-hidden="true">{open?'−':'+'}</span>
          </button>
          {open && <div id={`studio-library-${group.id}`} className="--TSD-library-items"><p>{group.description}</p>{group.rows.map(row=>{
            const key=getKey(row), meta=getMeta(row);
            const copies=selectedKeys.filter(value=>value.split('::instance:')[0]===key).length;
            return <div className="--TSD-library-item" key={key}>
              <button className="--TSD-library-add" type="button" aria-label={`Add ${meta.title}`} onClick={()=>onAdd(row)}><span aria-hidden="true">＋</span><span>{meta.title}{copies>0&&<small>{copies} on dashboard</small>}</span></button>
              <Info row={row}/>
            </div>;
          })}</div>}
        </div>;
      })}
    </div>
  </section>;
}
